import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import ImageUpload from "../../components/ImageUpload";
import CatalogImporter from "../catalog/CatalogImporter";
import { Plus, Pencil, Trash2, Package, Clock, Layers, ShieldCheck, History, ChevronDown, Sparkles } from "lucide-react";
import { toast } from "sonner";

const TYPES = [
  { v: "prodotto", l: "Prodotto" }, { v: "servizio", l: "Servizio" },
  { v: "prenotazione", l: "Prenotazione" }, { v: "esperienza", l: "Esperienza" },
  { v: "corso", l: "Corso" }, { v: "evento", l: "Evento" }, { v: "abbonamento", l: "Abbonamento" },
];
const TYPE_LABEL = Object.fromEntries(TYPES.map((t) => [t.v, t.l]));
const EMPTY = {
  type: "prodotto", name: "", description: "", images: [], macro_category: "", category: "", subcategory: "",
  price: "", duration_min: "", quantity: "", availability: "", requires_booking: false, prepay: false,
  pay_onsite: true, availability_mode: "immediate", variants: [], attributes: {}, active: true,
};

export default function CatalogPanel() {
  const [items, setItems] = useState([]);
  const [taxonomy, setTaxonomy] = useState([]);
  const [tpl, setTpl] = useState(null);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [audit, setAudit] = useState([]);
  const [showAudit, setShowAudit] = useState(false);
  const [importing, setImporting] = useState(false);

  const load = () => api.get("/business/catalog").then(({ data }) => setItems(data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => { load(); api.get("/taxonomy").then(({ data }) => setTaxonomy(data.taxonomy || [])).catch(() => {}); api.get("/business/catalog/audit").then(({ data }) => setAudit(data || [])).catch(() => {}); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setAttr = (k, v) => setForm((f) => ({ ...f, attributes: { ...f.attributes, [k]: v } }));
  const cats = useMemo(() => (taxonomy.find((t) => t.macro === form.macro_category)?.categories) || [], [taxonomy, form.macro_category]);

  const loadTemplate = async (category, applyDefaults) => {
    if (!category) { setTpl(null); return; }
    try {
      const { data } = await api.get(`/business/catalog/template/${encodeURIComponent(category)}`);
      setTpl(data);
      if (applyDefaults) setForm((f) => ({ ...f, type: data.default_type || f.type }));
    } catch { setTpl(null); }
  };

  const openNew = () => { setEditing(null); setForm(EMPTY); setTpl(null); setOpen(true); };
  const openEdit = (it) => {
    setEditing(it.id);
    setForm({ ...EMPTY, ...it, price: it.price ?? "", duration_min: it.duration_min ?? "", quantity: it.quantity ?? "", images: it.images || [], variants: it.variants || [], attributes: it.attributes || {} });
    loadTemplate(it.category, false);
    setOpen(true);
  };
  const onCategory = (v) => { setForm((f) => ({ ...f, category: v, subcategory: "" })); loadTemplate(v, true); };

  const addVariant = (name = "") => setForm((f) => ({ ...f, variants: [...f.variants, { name, price: "", duration_min: "", max_participants: "" }] }));
  const setVariant = (i, k, v) => setForm((f) => ({ ...f, variants: f.variants.map((x, j) => j === i ? { ...x, [k]: v } : x) }));
  const rmVariant = (i) => setForm((f) => ({ ...f, variants: f.variants.filter((_, j) => j !== i) }));

  const save = async () => {
    if (!form.name.trim()) return toast.error("Il nome è obbligatorio");
    try {
      if (editing) await api.put(`/business/catalog/${editing}`, form);
      else await api.post("/business/catalog", form);
      setOpen(false); load(); toast.success(editing ? "Elemento aggiornato" : "Elemento creato");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (it) => {
    if (!window.confirm(`Eliminare "${it.name}"?`)) return;
    try { await api.delete(`/business/catalog/${it.id}`); load(); toast.success("Elemento eliminato"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const shown = filter === "all" ? items : items.filter((i) => i.type === filter);
  const commonHas = (k) => !tpl || (tpl.common_fields || []).includes(k) || editing;

  return (
    <div className="space-y-4" data-testid="catalog-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h3 className="font-semibold flex items-center gap-2"><Package className="h-5 w-5 text-[#00E676]" /> Catalogo</h3>
        <Button data-testid="catalog-new-btn" onClick={openNew} className="bg-[#00E676] text-black rounded-md"><Plus className="h-4 w-4 mr-1" /> Nuovo elemento</Button>
      </div>
      <p className="text-sm text-zinc-500 -mt-2">Crea liberamente prodotti, servizi, prenotazioni, esperienze, corsi, eventi. La categoria carica automaticamente i campi giusti.</p>

      {audit.length > 0 && (
        <div className="rounded-xl border border-[#FF9F45]/30 bg-[#FF9F45]/5 overflow-hidden" data-testid="owner-audit-section">
          <button data-testid="owner-audit-toggle" onClick={() => setShowAudit((s) => !s)} className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left">
            <span className="flex items-center gap-2 text-sm font-semibold text-[#FF9F45]"><ShieldCheck className="h-4 w-4" /> Storico modifiche Centrale · {audit.length}</span>
            <ChevronDown className={`h-4 w-4 text-zinc-400 transition-transform ${showAudit ? "rotate-180" : ""}`} />
          </button>
          {showAudit && (
            <div className="px-4 pb-3 max-h-64 overflow-y-auto space-y-1.5" data-testid="owner-audit-list">
              <p className="text-xs text-zinc-500 mb-2">Modifiche effettuate dalla Centrale Fuma per conto della tua attività. Piena trasparenza: chi, cosa, quando.</p>
              {audit.map((a, i) => {
                const ACTIONS = { create: "ha creato", update: "ha modificato", delete: "ha eliminato", duplicate: "ha duplicato", availability: "ha aggiornato la disponibilità di", reorder: "ha riordinato il catalogo" };
                const name = a.after?.name || a.before?.name || "";
                return (
                  <div key={i} data-testid={`owner-audit-row-${i}`} className="text-xs text-zinc-300 border-b border-white/5 pb-1.5 last:border-0 flex items-start gap-2">
                    <History className="h-3.5 w-3.5 text-[#FF9F45] mt-0.5 shrink-0" />
                    <span className="flex-1 min-w-0">
                      <span className="text-[#FF9F45] font-semibold">{a.actor_name || "Centrale"}</span> {ACTIONS[a.action] || a.action} {name && <span className="text-white font-medium">"{name}"</span>}
                      {a.action === "update" && a.before && a.after && Number(a.before.price) !== Number(a.after.price) && (
                        <span className="text-zinc-500"> · prezzo €{Number(a.before.price || 0).toFixed(2)} → €{Number(a.after.price || 0).toFixed(2)}</span>
                      )}
                      <span className="block text-zinc-600">{a.at ? new Date(a.at).toLocaleString("it-IT") : ""}</span>
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1 flex-1">
          {[{ v: "all", l: "Tutti" }, ...TYPES].map((t) => (
            <button key={t.v} data-testid={`catalog-filter-${t.v}`} onClick={() => setFilter(t.v)}
              className={`rounded-full px-3.5 py-1.5 text-sm border whitespace-nowrap ${filter === t.v ? "bg-[#00E676]/20 border-[#00E676]/50 text-[#00E676]" : "border-white/10 bg-white/5 text-zinc-400 hover:text-white"}`}>{t.l}</button>
          ))}
        </div>
        <button data-testid="catalog-import-ai" onClick={() => setImporting(true)} className="shrink-0 flex items-center gap-1.5 text-sm font-semibold bg-gradient-to-r from-[#7DF9FF] to-[#00E676] text-black rounded-full px-3.5 py-1.5">
          <Sparkles className="h-4 w-4" /> Importa con AI
        </button>
      </div>
      {importing && (
        <CatalogImporter parseUrl="/business/catalog/import/parse" commitUrl="/business/catalog/import/commit"
          onClose={() => setImporting(false)} onDone={() => { setImporting(false); load(); }} />
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {shown.map((it) => (
          <div key={it.id} data-testid={`catalog-item-${it.id}`} className="rounded-xl bg-[#1C1C1F] border border-white/10 p-4 flex flex-col gap-2">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-medium truncate">{it.name}</div>
                <div className="text-xs text-zinc-500">{TYPE_LABEL[it.type]}{it.category ? ` · ${it.category}` : ""}</div>
              </div>
              <span className="text-xs px-2 py-0.5 rounded-full bg-white/5 border border-white/10 shrink-0">{TYPE_LABEL[it.type]}</span>
            </div>
            <div className="flex items-center gap-3 text-xs text-zinc-400">
              {it.price > 0 && <span>€{Number(it.price).toFixed(2)}</span>}
              {it.duration_min && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{it.duration_min}′</span>}
              {it.variants?.length > 0 && <span className="flex items-center gap-1"><Layers className="h-3 w-3" />{it.variants.length} varianti</span>}
              {!it.active && <span className="text-amber-400">Nascosto</span>}
            </div>
            <div className="flex gap-2 mt-auto pt-1">
              <Button size="sm" variant="secondary" data-testid={`catalog-edit-${it.id}`} onClick={() => openEdit(it)}><Pencil className="h-3.5 w-3.5" /></Button>
              <Button size="sm" variant="secondary" data-testid={`catalog-del-${it.id}`} onClick={() => del(it)}><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
        ))}
        {shown.length === 0 && <div className="text-zinc-500 py-8 text-center col-span-full">Nessun elemento. Crea il primo con "Nuovo elemento".</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#131316] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Modifica elemento" : "Nuovo elemento"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Tipo</Label>
                <select data-testid="cat-type" value={form.type} onChange={(e) => set("type", e.target.value)} className="w-full mt-1 bg-[#0A0A0C] border border-white/10 rounded-lg px-3 py-2 text-sm">
                  {TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                </select>
              </div>
              <div><Label>Nome *</Label><Input data-testid="cat-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-[#0A0A0C] border-white/10 mt-1" /></div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div><Label>Macro-categoria</Label>
                <select data-testid="cat-macro" value={form.macro_category} onChange={(e) => setForm((f) => ({ ...f, macro_category: e.target.value, category: "", subcategory: "" }))} className="w-full mt-1 bg-[#0A0A0C] border border-white/10 rounded-lg px-3 py-2 text-sm">
                  <option value="">—</option>{taxonomy.map((t) => <option key={t.macro} value={t.macro}>{t.macro}</option>)}
                </select>
              </div>
              <div><Label>Categoria</Label>
                <select data-testid="cat-category" value={form.category} onChange={(e) => onCategory(e.target.value)} disabled={!form.macro_category} className="w-full mt-1 bg-[#0A0A0C] border border-white/10 rounded-lg px-3 py-2 text-sm">
                  <option value="">—</option>{cats.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
                </select>
              </div>
              <div><Label>Prezzo (€)</Label><Input data-testid="cat-price" type="number" value={form.price} onChange={(e) => set("price", e.target.value)} className="bg-[#0A0A0C] border-white/10 mt-1" /></div>
            </div>

            <div><Label>Descrizione</Label><Textarea data-testid="cat-desc" value={form.description} onChange={(e) => set("description", e.target.value)} className="bg-[#0A0A0C] border-white/10 mt-1" rows={2} /></div>
            <div><Label>Immagine</Label><div className="mt-1"><ImageUpload testid="cat-image" folder="catalog" value={form.images[0] || ""} onChange={(url) => set("images", url ? [url] : [])} /></div></div>

            {(commonHas("duration_min") || form.type !== "prodotto") && (
              <div><Label>Durata (minuti)</Label>
                <Input data-testid="cat-duration" type="number" value={form.duration_min} onChange={(e) => set("duration_min", e.target.value)} className="bg-[#0A0A0C] border-white/10 mt-1" placeholder="es. 60" />
                {tpl?.suggested_durations?.length > 0 && (
                  <div className="flex gap-2 mt-2">{tpl.suggested_durations.map((d) => (
                    <button key={d} type="button" onClick={() => set("duration_min", d)} className="text-xs rounded-full px-2.5 py-1 border border-white/10 bg-white/5 hover:border-[#00E676]/50">{d}′</button>
                  ))}</div>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div><Label>Quantità / posti</Label><Input data-testid="cat-qty" type="number" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} className="bg-[#0A0A0C] border-white/10 mt-1" /></div>
              <div><Label>Disponibilità (nota)</Label><Input data-testid="cat-availability" value={form.availability} onChange={(e) => set("availability", e.target.value)} className="bg-[#0A0A0C] border-white/10 mt-1" placeholder="es. Lun-Ven 9-18" /></div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <label className="flex items-center gap-2 text-sm"><Switch data-testid="cat-requires-booking" checked={form.requires_booking} onCheckedChange={(v) => set("requires_booking", v)} /> Prenotazione obbligatoria</label>
              <label className="flex items-center gap-2 text-sm"><Switch data-testid="cat-prepay" checked={form.prepay} onCheckedChange={(v) => set("prepay", v)} /> Pagamento anticipato</label>
              <label className="flex items-center gap-2 text-sm"><Switch data-testid="cat-payonsite" checked={form.pay_onsite} onCheckedChange={(v) => set("pay_onsite", v)} /> Pagamento in sede</label>
              <label className="flex items-center gap-2 text-sm"><Switch data-testid="cat-active" checked={form.active} onCheckedChange={(v) => set("active", v)} /> Visibile</label>
            </div>
            <div><Label>Modalità disponibilità</Label>
              <select data-testid="cat-avmode" value={form.availability_mode} onChange={(e) => set("availability_mode", e.target.value)} className="w-full mt-1 bg-[#0A0A0C] border border-white/10 rounded-lg px-3 py-2 text-sm">
                <option value="immediate">Disponibilità immediata</option>
                <option value="appointment">Su appuntamento</option>
              </select>
            </div>

            {tpl?.attributes?.length > 0 && (
              <div className="rounded-xl border border-white/10 bg-[#0A0A0C] p-3 space-y-3" data-testid="cat-attributes">
                <div className="text-sm font-semibold text-[#00E676]">Dettagli {form.category}</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {tpl.attributes.map((a) => (
                    <div key={a.key}>
                      <Label className="text-xs">{a.label}</Label>
                      {a.type === "bool" ? (
                        <label className="flex items-center gap-2 text-sm mt-1.5"><Switch data-testid={`attr-${a.key}`} checked={!!form.attributes[a.key]} onCheckedChange={(v) => setAttr(a.key, v)} /> {a.label}</label>
                      ) : a.type === "select" ? (
                        <select data-testid={`attr-${a.key}`} value={form.attributes[a.key] || ""} onChange={(e) => setAttr(a.key, e.target.value)} className="w-full mt-1 bg-[#131316] border border-white/10 rounded-lg px-3 py-2 text-sm">
                          <option value="">—</option>{a.options.map((o) => <option key={o} value={o}>{o}</option>)}
                        </select>
                      ) : (
                        <Input data-testid={`attr-${a.key}`} type={a.type === "number" ? "number" : "text"} value={form.attributes[a.key] || ""} onChange={(e) => setAttr(a.key, e.target.value)} className="bg-[#131316] border-white/10 mt-1" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="rounded-xl border border-white/10 bg-[#0A0A0C] p-3 space-y-2" data-testid="cat-variants">
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold">Varianti</div>
                <Button size="sm" variant="secondary" data-testid="cat-add-variant" onClick={() => addVariant()}><Plus className="h-3.5 w-3.5 mr-1" />Aggiungi</Button>
              </div>
              {tpl?.variant_suggestions?.length > 0 && form.variants.length === 0 && (
                <div className="flex gap-2 flex-wrap">{tpl.variant_suggestions.map((s) => (
                  <button key={s} type="button" onClick={() => addVariant(s)} className="text-xs rounded-full px-2.5 py-1 border border-white/10 bg-white/5 hover:border-[#00E676]/50">+ {s}</button>
                ))}</div>
              )}
              {form.variants.map((v, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`variant-row-${i}`}>
                  <Input placeholder="Nome" value={v.name} onChange={(e) => setVariant(i, "name", e.target.value)} className="col-span-4 bg-[#131316] border-white/10 h-9" />
                  <Input placeholder="€" type="number" value={v.price} onChange={(e) => setVariant(i, "price", e.target.value)} className="col-span-2 bg-[#131316] border-white/10 h-9" />
                  <Input placeholder="min" type="number" value={v.duration_min} onChange={(e) => setVariant(i, "duration_min", e.target.value)} className="col-span-2 bg-[#131316] border-white/10 h-9" />
                  <Input placeholder="max p." type="number" value={v.max_participants} onChange={(e) => setVariant(i, "max_participants", e.target.value)} className="col-span-3 bg-[#131316] border-white/10 h-9" />
                  <button onClick={() => rmVariant(i)} className="col-span-1 text-zinc-500 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <Button data-testid="cat-save" onClick={save} className="bg-[#00E676] text-black rounded-md flex-1">{editing ? "Salva modifiche" : "Crea elemento"}</Button>
            <Button variant="secondary" onClick={() => setOpen(false)}>Annulla</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
